import { useEffect, useMemo, useState } from 'react';
import { useNavigation, useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  ContextHeading,
  SectionHeading,
} from '@quickmd-llc/design-system';
import { Events, EventDirection, tracking } from '@lib';
import { isDateToday } from '@utils';
import isDateTomorrow from 'utils/datetime/isDateTomorrow';

export default function DayPicker(): JSX.Element {
  const { state } = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

  const isLoading = state === 'loading';

  const dateText = useMemo(() => {
    return formatDateText(selectedDate);
  }, [selectedDate]);

  const isSelectedDayToday = useMemo(() => {
    return isDateToday(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    const dateQuery = searchParams.get('date');
    const date = dateQuery ? dayjs(dateQuery) : dayjs();

    setSelectedDate(date);
  }, [searchParams]);

  function handleShowNextDay() {
    if (!selectedDate) {
      return;
    }

    const nextDay = selectedDate.add(1, 'day').format('YYYY-MM-DD');
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set('date', nextDay);
      return newParams;
    });

    tracking.trackEvent(Events.CHANGE_DATE, { Direction: EventDirection.NEXT });
  }

  function handleShowPreviousDay() {
    if (!selectedDate) {
      return;
    }

    const previousDay = selectedDate.subtract(1, 'day').format('YYYY-MM-DD');
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set('date', previousDay);
      return newParams;
    });

    tracking.trackEvent(Events.CHANGE_DATE, { Direction: EventDirection.PREV });
  }

  return (
    <div className="flex items-center justify-between">
      <Button
        priority="secondary"
        onClick={handleShowPreviousDay}
        disabled={isSelectedDayToday || isLoading}
        isIcon
      >
        <ArrowLeftIcon />
      </Button>
      <SectionHeading className="hidden md:block">{dateText}</SectionHeading>
      <ContextHeading className="block md:hidden">{dateText}</ContextHeading>
      <Button
        priority="secondary"
        onClick={handleShowNextDay}
        disabled={isLoading}
        isIcon
      >
        <ArrowRightIcon />
      </Button>
    </div>
  );
}

function formatDateText(date?: Dayjs | null): string | undefined {
  if (!date) {
    return;
  }

  const formattedDate = date.format('MMM D');

  if (isDateToday(date)) {
    return `Today, ${formattedDate}`;
  }

  if (isDateTomorrow(date)) {
    return `Tomorrow, ${formattedDate}`;
  }

  return formattedDate;
}
